import { Mail, Sparkles, GitBranch, LineChart, Shield, Zap } from "lucide-react";
import { useInView } from "../hooks/useInView";

const features = [
  {
    icon: Mail,
    title: "Gmail Integration",
    description: "Connect your Google Workspace to send and receive emails directly from the CRM. Every thread auto-linked to the right contact.",
    span: "col-span-1 lg:col-span-2",
    bg: "bg-[#f6f2f5]",
  },
  {
    icon: Sparkles,
    title: "AI Email Drafts",
    description: "Claude AI drafts personalized follow-ups with full context of your past conversations. Every word intentional.",
    span: "col-span-1",
    bg: "bg-[#f0edf0]",
  },
  {
    icon: GitBranch,
    title: "Intro Path Tracking",
    description: "See exactly who introduced you to whom. Discover the strongest intro paths through your existing network.",
    span: "col-span-1",
    bg: "bg-[#f0edf0]",
  },
  {
    icon: LineChart,
    title: "Relationship Graph",
    description: "Full visual mapping of your network. Track contact warmth decay over time. Know when investors are going cold before they ghost.",
    span: "col-span-1 lg:col-span-2",
    bg: "bg-[#f6f2f5]",
  },
  {
    icon: Shield,
    title: "Commitment Tracking",
    description: "Automatically detect commitments made in emails. Never miss a promise or a follow-up again.",
    span: "col-span-1",
    bg: "bg-[#f6f2f5]",
  },
  {
    icon: Zap,
    title: "Morning Digest",
    description: "Every morning, Relio surfaces a prioritized digest of who to reach out to, what changed, and what's urgent.",
    span: "col-span-1 lg:col-span-2",
    bg: "bg-[#f0edf0]",
  },
];

export default function FeatureShowcase() {
  const [sectionRef, isInView] = useInView();

  return (
    <section
      id="features"
      data-testid="features-section"
      ref={sectionRef}
      className="pl-8 md:pl-16 pr-4 md:pr-8 py-16 md:py-24"
    >
      {/* Section header */}
      <div
        className={`mb-16 transition-all duration-700 ${
          isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#a33900] block mb-4">
          Feature Set
        </span>
        <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#1c1b1d]">
          Built for how founders
          <br />
          actually work.
        </h2>
        <p className="mt-4 text-base md:text-lg text-[#5a4138] max-w-lg">
          Every feature designed to reduce manual work and surface the signals that matter.
        </p>
      </div>

      {/* Bento grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {features.map((feature, i) => {
          const Icon = feature.icon;
          return (
            <div
              key={feature.title}
              data-testid={`feature-${feature.title.toLowerCase().replace(/\s/g, '-')}`}
              className={`
                ${feature.span} ${feature.bg} rounded-3xl p-8
                group hover:bg-[#e2bfb2]/20 transition-all duration-500
                ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}
              `}
              style={{ transitionDelay: `${i * 80 + 150}ms` }}
            >
              <div className="w-10 h-10 rounded-xl bg-white/80 flex items-center justify-center mb-5 group-hover:bg-white transition-colors">
                <Icon size={18} className="text-[#a33900]" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-medium text-[#1c1b1d] mb-2">{feature.title}</h3>
              <p className="text-sm text-[#5a4138] leading-relaxed">{feature.description}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}
